import { ChartLineIcon } from '@phosphor-icons/react'
import { createElement } from 'react'
import type { NavItemsInput, NavListEntry, NavSection } from 'app/types/navigation'
import { RECORD_TYPES, recordTypeRow } from 'lib/recordTypes'
import { OPS_RAIL } from 'lib/opsSections'
import { PAGE_RAILS } from 'lib/pageRails'
import { railSectionLabel } from 'lib/railSections'
import {
  actionCenterItem,
  agentsHubItem,
  analyticsChildren,
  companiesItem,
  inboxItem,
  opsItem,
  relatedContactsPrototypeItem,
  settingsItem,
  workflowsItem,
} from 'lib/sidebarItems'
import type { NavItemType } from 'types/Sidebar'

/** Tasks is one row under Work; completed tasks sit behind the page's own toggle. */
function tasksItem(input: NavItemsInput): NavItemType {
  const { counts, location } = input
  return {
    name: `Tasks`,
    icon: RECORD_TYPES.tasks.icon,
    ...RECORD_TYPES.tasks.gate(input),
    href: `/tasks?tab=open-tasks`,
    active: !!location.pathname.match(RECORD_TYPES.tasks.path),
    count: counts.tasks?.[`open-tasks`],
    countColor: counts.hasOverdueMyTasks ? `text-red-600` : undefined,
  }
}

/**
 * One row for every dashboard. The row itself carries no product, so an AI Agent only
 * workspace still reaches Voice Agent; each child keeps the gate its old parent carried,
 * and the row disappears when none survive. Conversions is super-user only, so it sits last.
 */
function analyticsGroup(input: NavItemsInput): NavItemType {
  const reports = analyticsChildren(input).map((item) => ({ ...item, product: item.product ?? `CRM` }))
  return {
    name: `Analytics`,
    icon: () => createElement(ChartLineIcon, { size: 20, weight: `bold` }),
    active: !!input.location.pathname.match(/^\/analytics\/?/),
    children: [
      ...reports.filter((item) => item.name !== `Conversions`),
      ...reports.filter((item) => item.name === `Conversions`),
    ],
  }
}

export function getNavSections(input: NavItemsInput): NavSection[] {
  if (input.user?.id == null) return []
  const settings = settingsItem(input)
  const ops = opsItem(input)
  return [
    { id: `top`, items: [companiesItem(input)] },
    {
      // No heading: no one title fits Action Center, Analytics and Workflows together.
      id: `work`,
      items: [
        actionCenterItem(input),
        inboxItem(input),
        tasksItem(input),
        analyticsGroup(input),
        // One row: the Agents page's own tabs replace the submenu.
        { ...agentsHubItem(input), rowOnly: true },
        workflowsItem(input),
        relatedContactsPrototypeItem(input),
      ],
    },
    {
      id: `records`,
      label: `Records`,
      items: [
        recordTypeRow(RECORD_TYPES.contacts, input),
        recordTypeRow(RECORD_TYPES.activities, input),
        recordTypeRow(RECORD_TYPES.treatmentPlans, input),
        recordTypeRow(RECORD_TYPES.appointments, input),
      ],
    },
    {
      id: `footer`,
      items: [
        {
          ...settings,
          // One row in the grouped sidebar; its children are the Settings page's rail, and
          // the page (SettingsFrame) reads them from this very item after the same filtering.
          rowOnly: true,
          href: `/settings`,
          // `settings.active` also covers Ops' own children (they share the `/settings` path,
          // e.g. feature toggles); an active Ops child draws its own pill, so the row itself
          // stays unlit there.
          active: settings.active && !ops.children?.some((child) => child.active),
          children: settings.children ?? [],
        },
        {
          ...ops,
          // One row too, like Settings: its children are the Ops page's rail (RailFrame).
          rowOnly: true,
          href: OPS_RAIL.homePath,
          active: OPS_RAIL.routes.some((route) => route.test(input.location.pathname)),
        },
      ],
    },
  ]
}

export function navDocumentTitle(sections: NavSection[], lists: NavListEntry[]): string | undefined {
  const activeList = lists.find((entry) => entry.item.active)
  if (activeList) {
    // A system or manual list has its own page and its own name, and so does a contacts
    // custom page now that it resolves to `/lists/<id>`; a preset, or a custom page still
    // on its record page (activities, tasks), is a filter, so that page's label wins.
    const hasOwnPage =
      activeList.source === `system` || activeList.source === `manual` || activeList.item.href?.startsWith(`/lists/`)
    return hasOwnPage ? activeList.item.name : RECORD_TYPES[activeList.recordType].label
  }
  for (const item of sections.flatMap((section) => section.items)) {
    // A row-only item (Settings, Ops) is titled by the section its page is on, as before it
    // became a row, under the same name its rail gives that section.
    if (item.rowOnly) {
      const activeSection = item.children?.find((child) => child.active)
      const rail = PAGE_RAILS.find((candidate) => candidate.rowName === item.name)
      if (activeSection) return rail ? railSectionLabel(rail, activeSection) : activeSection.name
    }
    if (item.href != null && item.active) return item.name
    const activeChild = item.children?.find((child) => child.active)
    if (activeChild) return activeChild.name
    if (item.children && item.active) return item.name
  }
  return undefined
}
